/**
 * The two min/max fieldsets of a clicker config form:
 *
 *   "Payout settings"  — the initial-amount bounds handed to the clicker as
 *                        MIN_INITIAL_AMOUNT / MAX_INITIAL_AMOUNT (which tasks it accepts).
 *   "Deposit settings" — the balance band handed to the clicker as
 *                        DEPOSIT_MIN_BALANCE / DEPOSIT_MAX_BALANCE, used by the `auto`
 *                        task to decide when to switch between deposits and payouts.
 *
 * EVERY amount here is in MINORS (integer cents), end to end: what is typed into these
 * inputs is stored, passed to the clicker and compared against the app balance verbatim,
 * with no scaling anywhere. So 200 000 in major units is entered as 20000000.
 *
 * That last sentence is exactly what operators keep misreading, so the inputs GROUP the
 * digits as they are typed — `20000000` shows as `200 000 00`, the last pair being the
 * minors. It is display only: the grouping is stripped on read, nothing is scaled, and
 * the value that leaves this file is the same integer it always was. Scaling to major
 * units instead would mean touching the clicker, the DB and every stored bound; a
 * separator makes the same number readable without moving that boundary.
 *
 * Three forms need both pairs: the per-farm clicker config and the two per-device
 * surfaces (launch panel + reconfigure modal). One implementation so they can never
 * drift on labels, validation or the meaning of an empty field — the only difference is
 * the placeholder, which spells out what an empty field falls back to ("9000000" / "0"
 * for the farm defaults, "farm default" for a device override). The "(minors)" unit is
 * part of the LABEL here, not something the callers add, so no form can forget it.
 */
import { t } from '../ui/lang';
// The unit rides in the LABEL, not only in the placeholder: a placeholder disappears the
// moment a field is filled, which is exactly when the number is long enough to be
// misread. Validation messages use the label, so they say "minors" too, for free.
const PAYOUT_FIELDS = {
    minClass: 'clicker-amount-min',
    maxClass: 'clicker-amount-max',
    minLabel: t('clicker.amount_min_initial_label'),
    maxLabel: t('clicker.amount_max_initial_label'),
};
const DEPOSIT_FIELDS = {
    minClass: 'clicker-deposit-balance-min',
    maxClass: 'clicker-deposit-balance-max',
    minLabel: t('clicker.amount_min_balance_label'),
    maxLabel: t('clicker.amount_max_balance_label'),
};
// Narrow no-break space: it groups digits without ever wrapping a number across lines
// and without reading as a word boundary on copy.
const GROUP_SEP = ' ';
/**
 * Digits grouped for reading: the last two are the minors, the rest in threes.
 * `20000000` → `200 000 00`. Anything shorter than three digits is left alone — while
 * typing, "5" must not jump to "0 05".
 */
export function formatMinorsGrouped(raw) {
    const digits = String(raw ?? '').replace(/\D/g, '');
    if (digits.length <= 2)
        return digits;
    const major = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEP);
    return `${major}${GROUP_SEP}${digits.slice(-2)}`;
}
/** The grouped text back to the integer minors it displays. Empty stays empty. */
function stripGrouping(value) {
    return value.replace(/\D/g, '');
}
/** Placeholders are numbers on the farm form and words ("farm default") on the device
 *  ones — group only the former, so the hint reads the way the field will. */
function formatPlaceholder(text) {
    return /^\d+$/.test(text) ? formatMinorsGrouped(text) : text;
}
/**
 * Make the amount inputs group as they are typed. Call once per rendered form.
 *
 * The caret is restored by counting DIGITS before it rather than characters: inserting a
 * separator shifts every later character, so a plain offset would walk the caret
 * backwards through the number on every keystroke.
 */
export function wireAmountGroups(root) {
    const selector = [PAYOUT_FIELDS, DEPOSIT_FIELDS]
        .flatMap((f) => [`.${f.minClass}`, `.${f.maxClass}`])
        .join(',');
    root.querySelectorAll(selector).forEach((input) => {
        input.addEventListener('input', () => {
            const caret = input.selectionStart ?? input.value.length;
            const digitsBefore = stripGrouping(input.value.slice(0, caret)).length;
            const formatted = formatMinorsGrouped(input.value);
            if (formatted === input.value)
                return;
            input.value = formatted;
            // Walk forward until we have passed the same number of digits.
            let seen = 0;
            let pos = 0;
            while (pos < formatted.length && seen < digitsBefore) {
                if (/\d/.test(formatted[pos]))
                    seen++;
                pos++;
            }
            input.setSelectionRange(pos, pos);
        });
    });
}
function pairGroupHtml(fields, legend, opts) {
    // Collapsible (native <details>, same marker idiom as the "How it works" block on
    // the code tab). Both groups are optional most of the time, so they stay out of the
    // way — but a group that already carries a bound opens itself, so a configured value
    // is never hidden behind a click. Zero counts as "no bound", not as a value: the farm
    // form always sends min_initial_amount (NOT NULL DEFAULT 0), and 0 means "no floor" /
    // "no ceiling" anyway — so a `!= null` test would leave that group permanently open.
    const open = (opts.min ?? 0) > 0 || (opts.max ?? 0) > 0;
    return `
        <details class="clicker-amount-group"${open ? ' open' : ''}>
            <summary class="clicker-amount-group__title">${legend}</summary>
            <div class="clicker-amount-group__fields">
                <div class="modal__field">
                    <label>${fields.minLabel}</label>
                    <input class="settings-input ${fields.minClass}" type="text" inputmode="numeric" autocomplete="off"
                           placeholder="${formatPlaceholder(opts.minPlaceholder)}"
                           value="${formatMinorsGrouped(opts.min)}" />
                </div>
                <div class="modal__field">
                    <label>${fields.maxLabel}</label>
                    <input class="settings-input ${fields.maxClass}" type="text" inputmode="numeric" autocomplete="off"
                           placeholder="${formatPlaceholder(opts.maxPlaceholder)}"
                           value="${formatMinorsGrouped(opts.max)}" />
                </div>
            </div>
        </details>
    `;
}
function readPair(root, fields) {
    const parse = (sel, label) => {
        const el = root.querySelector(sel);
        // Grouping is display only — strip it here so the value that leaves this file is
        // the same integer the field has always carried.
        const raw = stripGrouping(el ? el.value : '');
        if (raw === '')
            return null;
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n) || n < 0)
            return { error: t('clicker.amount_non_negative', { label }) };
        return n;
    };
    const min = parse(`.${fields.minClass}`, fields.minLabel);
    if (min !== null && typeof min === 'object')
        return min;
    const max = parse(`.${fields.maxClass}`, fields.maxLabel);
    if (max !== null && typeof max === 'object')
        return max;
    if (min != null && max != null && min > max) {
        return { error: t('clicker.amount_min_exceeds_max', { min: fields.minLabel, max: fields.maxLabel.toLowerCase() }) };
    }
    return { min, max };
}
/**
 * Markup for the payout group. Both inputs are optional: an empty one reads back as
 * null, which the server stores as "no value" (farm: falls back to the clicker default;
 * device: falls back to the farm value).
 */
export function amountBoundsGroupHtml(opts) {
    return pairGroupHtml(PAYOUT_FIELDS, t('clicker.payout_settings'), opts);
}
/**
 * Read + validate the payout group. Returns the bounds, or `{ error }` with a
 * ready-to-show message (non-negative integers, and min must not exceed max).
 */
export function readAmountBounds(root) {
    return readPair(root, PAYOUT_FIELDS);
}
/** Markup for the deposit group — the `auto` task's balance band. Same empty semantics. */
export function depositBalanceGroupHtml(opts) {
    return pairGroupHtml(DEPOSIT_FIELDS, t('clicker.deposit_settings'), opts);
}
/** Read + validate the deposit group. */
export function readDepositBalance(root) {
    return readPair(root, DEPOSIT_FIELDS);
}
/**
 * Both groups as ONE row — this is what the three forms embed, not the two groups
 * concatenated. Stacking them made the forms (the reconfigure modal above all) taller
 * than the viewport; side by side they cost one block instead of two. The container
 * collapses back to a single column when the form is too narrow for two, so nothing
 * breaks on a phone or in a cramped panel.
 */
export function amountGroupsRowHtml(payout, deposit) {
    return `
        <div class="clicker-amount-groups">
            ${amountBoundsGroupHtml(payout)}
            ${depositBalanceGroupHtml(deposit)}
        </div>
    `;
}
